import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import "moment/locale/it";
import apiClient from "../api/client";
import { FaTrash, FaEdit, FaChevronDown, FaChevronUp } from "react-icons/fa";
import useAuth from "../auth/useAuth"; // Adjust the import path as needed
import ExportModal from "../components/ExportModal";
import { saveAs } from "file-saver";

moment.locale("it");

export default function CommesseHistory() {
  const { machineName } = useParams();
  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCommessa, setEditingCommessa] = useState(null);
  const [showNotesFor, setShowNotesFor] = useState({});
  const [replyTexts, setReplyTexts] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useAuth();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Add form state for editing
  const [editForm, setEditForm] = useState({
    nome: "",
    data_ora: "",
    pezzi: 0,
    note: "",
    nome_cliente: "",
  });

  // Add downloadProgress state
  const [downloadProgress, setDownloadProgress] = useState({});

  useEffect(() => {
    fetchCommesse();
  }, [machineName]);

  const fetchCommesse = async () => {
    try {
      const response = await apiClient.get(
        `api/commesse/machine/${machineName}`
      );
      if (response.ok) {
        setCommesse(response.data);
      } else {
        setError("Errore nel caricamento delle commesse");
      }
    } catch (error) {
      console.error("Error fetching commesse:", error);
      setError("Errore nel caricamento delle commesse");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (commessaId, newStatus) => {
    try {
      const response = await apiClient.patch(
        `api/commesse/${commessaId}/status`,
        {
          status: newStatus,
        }
      );

      if (response.ok) {
        fetchCommesse();
      } else {
        if (response.data.error) {
          setError(response.data.error);
        } else {
          setError("Errore durante il cambio di stato");
        }
      }
    } catch (error) {
      console.error("Error changing status:", error);
      setError("Errore durante il cambio di stato");
    }
  };

  const handleDelete = async (commessaId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa commessa?"))
      return;

    try {
      const response = await apiClient.delete(`api/commesse/${commessaId}`);
      if (response.ok) {
        fetchCommesse();
      } else {
        setError("Errore durante l'eliminazione della commessa");
      }
    } catch (error) {
      console.error("Error deleting commessa:", error);
      setError("Errore durante l'eliminazione della commessa");
    }
  };

  const handleEdit = (commessa) => {
    setEditingCommessa(commessa);
    setEditForm({
      nome: commessa.nome,
      data_ora: moment(commessa.data_ora).format("YYYY-MM-DDTHH:mm"),
      pezzi: commessa.pezzi,
      note: commessa.note || "",
      nome_cliente: commessa.nome_cliente || "",
    });
    setIsModalOpen(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.put(
        `api/commesse/${editingCommessa.id}`,
        {
          ...editForm,
          data_ora: moment(editForm.data_ora).format("YYYY-MM-DD HH:mm:ss"),
        }
      );

      if (response.ok) {
        setIsModalOpen(false);
        fetchCommesse();
      } else {
        setError("Errore durante l'aggiornamento della commessa");
      }
    } catch (error) {
      console.error("Error updating commessa:", error);
      setError("Errore durante l'aggiornamento della commessa");
    }
  };

  const handleAddReply = async (commessaId) => {
    const replyText = replyTexts[commessaId];
    if (!replyText?.trim()) return;

    try {
      const response = await apiClient.post(
        `api/commesse/${commessaId}/replies`,
        {
          message: replyText.trim(),
          operator: user.username || user.email || "Operatore",
        }
      );

      if (response.ok) {
        setReplyTexts((prev) => ({
          ...prev,
          [commessaId]: "",
        }));
        fetchCommesse();
      } else {
        setError("Errore durante l'aggiunta della nota");
      }
    } catch (error) {
      console.error("Error adding reply:", error);
      setError("Errore durante l'aggiunta della nota");
    }
  };

  const calculateDuration = (commessa) => {
    if (
      commessa.stato !== "completata" ||
      !commessa.completed_at ||
      !commessa.started_at
    )
      return "-";

    const startDate = moment(commessa.started_at);
    const endDate = moment(commessa.completed_at);
    const duration = moment.duration(endDate.diff(startDate));

    if (duration.asHours() >= 24) {
      return `${Math.floor(duration.asDays())}g ${duration.hours()}h`;
    }
    return `${duration.hours()}h ${duration.minutes()}m`;
  };

  const toggleNotes = (commessaId) => {
    setShowNotesFor((prev) => ({
      ...prev,
      [commessaId]: !prev[commessaId],
    }));
  };

  // Add handleDownloadFile function
  const handleDownloadFile = async (fileId, originalFilename) => {
    try {
      const response = await apiClient.get(
        `api/commesse/files/${fileId}`,
        {},
        {
          responseType: "blob",
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setDownloadProgress((prev) => ({
                ...prev,
                [fileId]: percentCompleted,
              }));
            }
          },
        }
      );

      if (response.ok) {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = originalFilename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Error downloading file");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      // Clear progress after a short delay
      setTimeout(() => {
        setDownloadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
      }, 1000);
    }
  };

  const handleExport = async (dateRange) => {
    try {
      const response = await apiClient.get(
        `api/commesse/export/${machineName}?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
        {},
        { responseType: "blob" }
      );

      if (response.ok) {
        const blob = new Blob([response.data], {
          type: "text/csv;charset=utf-8",
        });
        saveAs(
          blob,
          `commesse_${machineName}_${dateRange.startDate}_${dateRange.endDate}.csv`
        );
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      setError("Errore durante l'esportazione dei dati");
    }
  };

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen p-6"
      >
        <div className="mx-auto">
          <div className="flex justify-between items-center mb-8">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl font-bold text-gray-800 mb-8"
            >
              Storico Commesse - {machineName}
            </motion.h1>
            <button
              onClick={() => setIsExportModalOpen(true)}
              className="btn btn-primary btn-sm"
            >
              Esporta CSV
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="alert alert-error mb-10 text-white"
            >
              {error}
            </motion.div>
          )}

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data/Ora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pezzi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inizio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fine
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durata
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commesse.map((commessa) => (
                    <>
                      <motion.tr key={commessa.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {moment(commessa.data_ora).format("DD/MM/YYYY HH:mm")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {commessa.nome}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {commessa.nome_cliente || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {commessa.pezzi} pezzi
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                commessa.stato === "completata"
                                  ? "bg-green-100 text-green-800"
                                  : commessa.stato === "annullata"
                                  ? "bg-red-100 text-red-800"
                                  : commessa.stato === "in_corso"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {commessa.stato === "in_corso"
                                ? "In Corso"
                                : commessa.stato === "in_attesa"
                                ? "In Attesa"
                                : commessa.stato === "completata"
                                ? "Completata"
                                : "Annullata"}
                            </span>
                            {commessa.stato === "in_attesa" && (
                              <button
                                onClick={() =>
                                  handleStatusChange(commessa.id, "in_corso")
                                }
                                className="btn btn-xs btn-primary"
                              >
                                In lavorazione
                              </button>
                            )}
                            {commessa.stato === "in_corso" && (
                              <button
                                onClick={() =>
                                  handleStatusChange(commessa.id, "completata")
                                }
                                className="btn btn-xs btn-success"
                              >
                                Completato
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {commessa.started_at
                            ? moment(commessa.started_at).format("DD/MM HH:mm")
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {commessa.completed_at
                            ? moment(commessa.completed_at).format(
                                "DD/MM HH:mm"
                              )
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {calculateDuration(commessa)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(commessa)}
                              disabled={commessa.stato === "completata"}
                              className={`text-blue-600 hover:text-blue-800 ${
                                commessa.stato === "completata"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              <FaEdit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(commessa.id)}
                              disabled={commessa.stato === "completata"}
                              className={`text-red-600 hover:text-red-800 ${
                                commessa.stato === "completata"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => toggleNotes(commessa.id)}
                              className="text-gray-600 hover:text-gray-800 flex items-center space-x-2"
                            >
                              {showNotesFor[commessa.id] ? (
                                <FaChevronUp className="w-4 h-4" />
                              ) : (
                                <FaChevronDown className="w-4 h-4" />
                              )}
                              <div className="flex space-x-1">
                                {commessa.files &&
                                  commessa.files.length > 0 && (
                                    <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                                      {commessa.files.length} file
                                      {commessa.files.length !== 1 ? "s" : ""}
                                    </span>
                                  )}
                                {commessa.note && (
                                  <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                                    Note
                                  </span>
                                )}
                                {commessa.replies &&
                                  commessa.replies.length > 0 && (
                                    <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                                      {commessa.replies.length} risposte
                                    </span>
                                  )}
                              </div>
                            </button>
                          </div>
                        </td>
                      </motion.tr>

                      {/* Expandable Notes Section */}
                      {showNotesFor[commessa.id] && (
                        <motion.tr
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="bg-gray-50"
                        >
                          <td colSpan="9" className="px-6 py-4">
                            <div className="space-y-4">
                              {/* Files section */}
                              {commessa.files && commessa.files.length > 0 && (
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    File allegati:
                                  </h4>
                                  <ul className="space-y-2">
                                    {commessa.files.map((file, idx) => {
                                      if (file.id)
                                        return (
                                          <li
                                            key={idx}
                                            className="flex flex-col space-y-1"
                                          >
                                            <button
                                              onClick={() =>
                                                handleDownloadFile(
                                                  file.id,
                                                  file.original_filename
                                                )
                                              }
                                              className="text-blue-600 hover:text-blue-800 flex items-center"
                                              disabled={
                                                downloadProgress[file.id] !==
                                                undefined
                                              }
                                            >
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-4 w-4 mr-1"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                                />
                                              </svg>
                                              {file.original_filename}
                                            </button>
                                            {downloadProgress[file.id] !==
                                              undefined && (
                                              <div className="w-full">
                                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                  <div
                                                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                                    style={{
                                                      width: `${
                                                        downloadProgress[
                                                          file.id
                                                        ]
                                                      }%`,
                                                    }}
                                                  ></div>
                                                </div>
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                  Download in corso:{" "}
                                                  {downloadProgress[file.id]}%
                                                </div>
                                              </div>
                                            )}
                                          </li>
                                        );
                                    })}
                                  </ul>
                                </div>
                              )}

                              {/* Notes section */}
                              {commessa.note && (
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Note:
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    {commessa.note}
                                  </p>
                                </div>
                              )}

                              {/* Replies section */}
                              <div className="space-y-2">
                                <h4 className="text-sm font-medium text-gray-700">
                                  Note operatori:
                                </h4>
                                {commessa.replies &&
                                commessa.replies.length > 0 ? (
                                  <div className="space-y-2">
                                    {commessa.replies.map((reply, idx) => (
                                      <div
                                        key={idx}
                                        className="bg-white p-3 rounded-lg border border-gray-200"
                                      >
                                        <div className="flex justify-between items-center mb-1">
                                          <span className="text-sm font-medium text-gray-700">
                                            {reply.operator}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            {moment(reply.timestamp).format(
                                              "DD/MM/YY HH:mm"
                                            )}
                                          </span>
                                        </div>
                                        <p className="text-sm text-gray-600">
                                          {reply.message}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 italic">
                                    Nessuna nota degli operatori
                                  </p>
                                )}

                                {/* Add reply form */}

                                <form
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    handleAddReply(commessa.id);
                                  }}
                                  className="mt-4"
                                >
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={replyTexts[commessa.id] || ""}
                                      onChange={(e) =>
                                        setReplyTexts((prev) => ({
                                          ...prev,
                                          [commessa.id]: e.target.value,
                                        }))
                                      }
                                      className="input input-bordered input-sm flex-1"
                                      placeholder="Aggiungi una nota..."
                                    />
                                    <button
                                      type="submit"
                                      disabled={
                                        !replyTexts[commessa.id]?.trim()
                                      }
                                      className="btn btn-primary btn-sm"
                                    >
                                      Invia
                                    </button>
                                  </div>
                                </form>
                              </div>
                            </div>
                          </td>
                        </motion.tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Modifica Commessa</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmitEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Commessa
                </label>
                <input
                  type="text"
                  required
                  value={editForm.nome}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, nome: e.target.value }))
                  }
                  className="input input-bordered w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data e ora inizio
                </label>
                <input
                  type="datetime-local"
                  required
                  value={editForm.data_ora}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      data_ora: e.target.value,
                    }))
                  }
                  className="input input-bordered w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numero pezzi
                </label>
                <input
                  type="number"
                  required
                  value={editForm.pezzi}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, pezzi: e.target.value }))
                  }
                  className="input input-bordered w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Cliente
                </label>
                <input
                  type="text"
                  value={editForm.nome_cliente}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      nome_cliente: e.target.value,
                    }))
                  }
                  className="input input-bordered w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={editForm.note}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, note: e.target.value }))
                  }
                  className="textarea textarea-bordered w-full"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="btn btn-ghost"
                >
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  Salva Modifiche
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        title={`Esporta commesse ${machineName}`}
      />
    </Layout>
  );
}
